class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      // History already stores the full query; do not merge from the live URL (would fight back/forward).
      FacetFiltersForm.renderPage(searchParams, null, false, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /**
   * Sort/filter forms only send facet fields (e.g. sort_by). Section Rendering API requests must
   * still carry catalogue/department context or Liquid drops ?duration= / ?collection= and returns
   * the default product grid (wrong mode: live vs ended vs buy-now) while the address bar unchanged.
   */
  static mergeCollectionSectionContextParams(searchParams) {
    const path = window.location.pathname || '';
    try {
      const merged = new URLSearchParams(searchParams || '');
      const cur = new URLSearchParams(window.location.search || '');
      const mergeKeys = (keys) => {
        keys.forEach((key) => {
          const val = cur.get(key);
          if (val !== null && val !== '') merged.set(key, val);
        });
      };
      if (path.indexOf('/collections/catalogues') === 0) {
        mergeKeys(['collection', 'duration', 'past_catalog', 'filter-collection', 'status']);
      }
      const m = /^\/collections\/([^/]+)\/?$/.exec(path);
      const handle = m && m[1] ? m[1].toLowerCase() : '';
      const departmentHandles = {
        'jewellery-fine-watches': true,
        'fine-decorative-art': true,
        'luxury-handbags-accessories': true,
        antiques: true,
        collectibles: true,
      };
      if (handle && handle !== 'catalogues' && departmentHandles[handle]) {
        mergeKeys(['duration', 'past_catalog', 'filter-collection', 'status', 'collection']);
      }
      // Lot Archive / Buy Now: ?collection=all is View All
      // (snippets/collection-department-tiles.liquid). Keep it through rail updates so the
      // Department filter stays in the rail there; a department view has no Department
      // filter in the rail, so its tile stays the only way to change department.
      if (handle === 'lot-archive' || handle === 'buy-now') {
        mergeKeys(['collection']);
      }
      if (handle && handle !== 'catalogues') {
        var directCatalogueGrid = document.getElementById('product-grid');
        if (directCatalogueGrid && directCatalogueGrid.getAttribute('data-direct-auction-catalogue') === 'true') {
          mergeKeys(['filter.v.availability']);
        }
      }
      // A past catalogue is identified by its grid rather than its handle -- any collection
      // can be reached with ?past_catalog=1, not just the five departments handled above.
      // Dropping it would re-render the live grid mode and strip the param from the URL.
      if (handle && handle !== 'catalogues') {
        var endedCatalogueGrid = document.getElementById('product-grid');
        if (endedCatalogueGrid && endedCatalogueGrid.getAttribute('data-ended-auction-collection') === 'true') {
          mergeKeys(['past_catalog']);
        }
      }
      return merged.toString();
    } catch (e) {
      return searchParams;
    }
  }

  static renderPage(searchParams, event, updateURLHash = true, preserveCataloguesContext = true) {
    if (preserveCataloguesContext) {
      searchParams = FacetFiltersForm.mergeCollectionSectionContextParams(searchParams);
    }
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const gridContainer = document.getElementById('ProductGridContainer');
    const collection = gridContainer ? gridContainer.querySelector('.collection') : null;
    if (collection) collection.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  // Always clear the loading UI, even if a render step throws. The spinner/overlay are otherwise
  // only removed as a side-effect of renderProductGridContainer/renderProductCount, so any
  // exception before those run would strand the loader forever (e.g. empty-result facet toggles).
  static hideLoading() {
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    const gridContainer = document.getElementById('ProductGridContainer');
    const collection = gridContainer ? gridContainer.querySelector('.collection') : null;
    if (collection) collection.classList.remove('loading');

    const countContainer = document.getElementById('ProductCount');
    if (countContainer) countContainer.classList.remove('loading');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    if (countContainerDesktop) countContainerDesktop.classList.remove('loading');

    if (typeof window.staRemoveIdleCollectionLoadingOverlay === 'function') {
      window.staRemoveIdleCollectionLoadingOverlay();
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        console.error('Facet render failed:', error);
      })
      .finally(() => {
        FacetFiltersForm.hideLoading();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    try {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    } catch (error) {
      console.error('Facet render failed:', error);
    } finally {
      FacetFiltersForm.hideLoading();
    }
  }

  static renderProductGridContainer(html) {
    const parsedGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    const liveGrid = document.getElementById('ProductGridContainer');
    if (!parsedGrid || !liveGrid) return;
    liveGrid.innerHTML = parsedGrid.innerHTML;

    // Department tiles (Lot Archive / Buy Now) sit above the grid, outside what this
    // redraws, so keep their active state in step with the rail's Department checkbox.
    const liveTiles = document.getElementById('CollectionDepartmentTiles');
    if (liveTiles) {
      const parsedTiles = parsedGrid.ownerDocument.getElementById('CollectionDepartmentTiles');
      liveTiles.innerHTML = parsedTiles ? parsedTiles.innerHTML : '';
    }

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    var grid = document.getElementById('product-grid');
    if (grid && grid.getAttribute('data-direct-auction-catalogue') === 'true') {
      document.documentElement.classList.add('sta-live-catalogue-pending');
    }

    if (typeof window.staSyncAuctionCatalogueHeroLots === 'function') {
      window.staSyncAuctionCatalogueHeroLots();
    }
    if (typeof window.staEnrichDirectAuctionCatalogue === 'function') {
      window.staEnrichDirectAuctionCatalogue();
    }
    // Upcoming catalogues keep their server-rendered lots, so restore the ending-first order.
    if (
      grid &&
      grid.getAttribute('data-upcoming-auction-catalogue') === 'true' &&
      typeof window.staSortCatalogueGridByEndTime === 'function'
    ) {
      window.staSortCatalogueGridByEndTime(grid);
    }

    // A past/ended catalogue renders its lots from the auction API, so the markup that
    // just landed holds an empty grid and a data-past-product-ids narrowed to the
    // filtered lots. Re-run the past renderer to fill it; without this a filter change
    // leaves the shopper staring at a bare loading overlay.
    if (
      grid &&
      grid.getAttribute('data-ended-auction-collection') === 'true' &&
      typeof window.staRenderPastAuctionGrids === 'function'
    ) {
      window.staRenderPastAuctionGrids();
    }
  }

  static renderProductCount(html) {
    const parsedCount = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const count = parsedCount ? parsedCount.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const insertBeforeTarget = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (insertBeforeTarget) insertBeforeTarget.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    const closestJSFilter = event ? event.target.closest('.js-filter') : null;
    if (countsToRender && closestJSFilter) {
      const closestJSFilterID = closestJSFilter.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, closestJSFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        if (newFacetDetailsElement) {
          const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
            ? `.mobile-facets__close-button`
            : `.facets__summary`;
          const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

          const isTextInput = event.target.getAttribute('type') === 'text';

          if (newElementToActivate && !isTextInput) newElementToActivate.focus();
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      if (!source) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const drawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    if (drawer && typeof drawer.bindEvents === 'function') drawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  /**
   * Shopify builds a facet's remove link from the collection URL plus the surviving
   * filters, so every non-filter param goes with it -- ?past_catalog=1 included, which
   * is what puts the page in past-catalogue mode in the first place. That one is
   * collateral rather than something the link set out to clear, so it is carried over;
   * everything else about the href stays authoritative.
   */
  static keepPastCatalogueParam(searchParams) {
    try {
      const grid = document.getElementById('product-grid');
      if (!grid || grid.getAttribute('data-ended-auction-collection') !== 'true') return searchParams;
      const current = new URLSearchParams(window.location.search || '').get('past_catalog');
      if (!current) return searchParams;
      const next = new URLSearchParams(searchParams || '');
      if (next.has('past_catalog')) return searchParams;
      next.set('past_catalog', current);
      return next.toString();
    } catch (e) {
      return searchParams;
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    // Link href is authoritative (e.g. remove all); do not re-append stale catalogue context params.
    FacetFiltersForm.renderPage(FacetFiltersForm.keepPastCatalogueParam(url), event, true, false);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 * "In stock" and "Sold out" are one choice presented as two checkboxes.
 *
 * They can't be one filter: Shopify's Availability filter only knows inventory, and
 * an auction-won lot deliberately stays in stock so its winner can still pay for it
 * (see snippets/facet-sold-filter.liquid). "Sold out" is therefore backed by a
 * different query parameter entirely -- custom.lot_sold, or the lot_sold tag.
 *
 * Left alone the two would AND, and "in stock AND sold" matches nothing: a shopper
 * ticking "Sold out" on a search would get an empty grid, since search force-selects
 * In stock on first load (main-search.liquid). So ticking either one unticks the
 * other, which is what a shopper reading the group expects anyway.
 *
 * Delegated from the document because the facet rail is replaced wholesale by the
 * Section Rendering API on every filter change -- a listener bound to the inputs
 * themselves would not survive the first click. Runs on `change`, well inside the
 * 800ms debounce before the form is serialised.
 */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!input || input.tagName !== 'INPUT' || input.type !== 'checkbox') return;

  const form = input.closest('facet-filters-form form');
  if (!form) return;

  const isSold = input.hasAttribute('data-sta-sold-filter');
  const isAvailability = input.name === 'filter.v.availability';
  if (!isSold && !isAvailability) return;

  // On search, In stock carries a hidden second half (custom.lot_sold=false) so that
  // "in stock" means "in stock and not sold" -- see snippets/facets.liquid. Synced
  // before the checked-only early return below, because *unchecking* In stock has to
  // drop the "not sold" half too: left behind on its own it would go on excluding
  // sold lots from a rail the shopper believes they just cleared. Setting .checked
  // programmatically fires no event, so this cannot re-enter.
  if (isAvailability) {
    form.querySelectorAll('input[data-sta-instock-companion]').forEach((companion) => {
      companion.checked = input.checked;
    });
  }

  if (!input.checked) return;

  // "Sold out" clears both halves of In stock, not just the visible one -- the
  // companion shares its parameter, and sending lot_sold=false alongside
  // lot_sold=true would return nothing at all.
  const selector = isSold
    ? 'input[name="filter.v.availability"], input[data-sta-instock-companion]'
    : 'input[data-sta-sold-filter]';
  form.querySelectorAll(selector).forEach((other) => {
    if (other !== input) other.checked = false;
  });
});

/**
 * The Availability group's own "Clear" link is built by Shopify from that filter
 * alone, so it drops In stock and leaves "Sold out" ticked -- the group reads as
 * cleared while one of its boxes is still on. Rewritten here, in the capture phase,
 * before FacetRemove hands the href to onActiveFilterClick as authoritative.
 *
 * Only the sold value is stripped, never the whole parameter: on the tag fallback
 * "Sold out" shares filter.p.tag with Department, and deleting the parameter would
 * silently clear the shopper's department too.
 *
 * On search the same applies to In stock's hidden "not sold" half. Shopify builds
 * Availability's Clear link from that filter alone, so it drops filter.v.availability
 * and leaves custom.lot_sold=false stranded -- the group reads as cleared while sold
 * lots are still being filtered out. Both values are stripped here for that reason.
 */
document.addEventListener(
  'click',
  (event) => {
    const link = event.target && event.target.closest && event.target.closest('facet-remove a');
    if (!link) return;

    const group = link.closest('details');
    const sold = group && group.querySelector('input[data-sta-sold-filter]');
    // Looked up document-wide, not within the group: the active-filter chips are
    // remove links too and sit outside the facet rail entirely.
    const companion = document.querySelector('input[data-sta-instock-companion]');

    try {
      const url = new URL(link.href, window.location.origin);

      const drops = [];
      if (sold && sold.name) drops.push(sold);

      // In stock and its hidden "not sold" half are one control, so the companion
      // goes wherever availability goes. Keyed off the link's own destination
      // rather than which element was clicked: that covers the group's Clear link
      // and the In stock chip alike, while leaving the companion untouched when
      // some other group's Clear link keeps In stock on.
      if (companion && companion.name && url.searchParams.get('filter.v.availability') !== '1') {
        drops.push(companion);
      }
      if (drops.length === 0) return;

      // Grouped by parameter: sold and companion share one on the metafield source,
      // so a single pass has to drop both of their values from it.
      const dropByParam = new Map();
      drops.forEach((input) => {
        if (!dropByParam.has(input.name)) dropByParam.set(input.name, new Set());
        dropByParam.get(input.name).add(input.value);
      });

      dropByParam.forEach((values, name) => {
        const kept = url.searchParams.getAll(name).filter((value) => !values.has(value));
        url.searchParams.delete(name);
        kept.forEach((value) => url.searchParams.append(name, value));
      });
      link.href = url.toString();
    } catch (e) {}
  },
  true
);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

document.querySelectorAll('[data-products-filters-trigger]').forEach((trigger) => {
  const parent = trigger.closest('[data-products]');
  const productsBody = parent.querySelector('[data-products-body]');

  trigger.addEventListener('click', (event) => {
    event.preventDefault();

    if (productsBody) {
      productsBody.classList.toggle('_filters-hidden');
    }
  })
})